import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff, X } from "../../utils/icons";
import { useAuthStore } from "../../store/useAuthStore";
import { useAuthModalStore } from "../../store/useAuthModalStore";
import { showSuccess, showError } from "../../utils/toast";
import { GOOGLE_CLIENT_ID } from "../../utils/constants";

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

function GoogleLoginButton() {
  const { loginWithGoogle, isLoading } = useAuthStore();

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        const userInfo = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${response.access_token}` },
        }).then((res) => res.json());

        loginWithGoogle(
          userInfo.given_name + (userInfo.family_name || ""),
          userInfo.email,
          response.access_token
        );
        showSuccess("Logging in with Google...");
      } catch {
        showError("Error during Google login. Please try again.");
      }
    },
    onError: () => {
      showError("Google login failed. Please try again.");
    },
  });

  return (
    <button className="am-google" onClick={() => login()} disabled={isLoading}>
      <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
      <span>Login with Google</span>
    </button>
  );
}

const AuthModal = () => {
  const { isOpen, mode, referralCode: initialReferral, close, switchToLogin, switchToRegister } = useAuthModalStore();
  const {
    login, register, resetPassword, verifyTwoFactor,
    isLoading, error, clearError,
    showTwoFactor, tempLoginData,
  } = useAuthStore();

  const [loginUser, setLoginUser] = useState("");
  const [loginPwd, setLoginPwd] = useState("");
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");

  const [regUser, setRegUser] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPwd, setRegPwd] = useState("");
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [regCountry, setRegCountry] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [referral, setReferral] = useState(initialReferral || "");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [pwdErrors, setPwdErrors] = useState([]);

  useEffect(() => { if (initialReferral) setReferral(initialReferral); }, [initialReferral]);
  useEffect(() => {
    if (!isOpen) {
      clearError();
      setForgotMode(false);
      setForgotSent(false);
      setTwoFactorCode("");
    }
  }, [isOpen, clearError]);

  // Watch for successful login (isAuthenticated changes)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      showSuccess("Successfully logged in!");
      close();
    }
  }, [isAuthenticated, isOpen, close]);

  // Watch for errors from socket
  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error]);

  if (!isOpen) return null;

  const validatePwd = (p) => {
    const e = [];
    if (p.length < 8) e.push("8+ chars");
    if (!/[A-Z]/.test(p)) e.push("Uppercase");
    if (!/[a-z]/.test(p)) e.push("Lowercase");
    if (!/\d/.test(p)) e.push("Number");
    if (!/[@$!%*?&]/.test(p)) e.push("Special");
    return e;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!loginUser.trim() || !loginPwd.trim()) { showError("Please fill in all fields"); return; }
    login(loginUser, loginPwd);
  };

  const handleForgot = (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { showError("Please enter your email"); return; }
    resetPassword(
      forgotEmail,
      () => { setForgotSent(true); showSuccess("Password reset link sent to your email"); },
      (msg) => { showError(msg); }
    );
  };

  const handleRegister = (e) => {
    e.preventDefault();
    if (regUser.length < 3 || regUser.length > 16) { showError("Username must be 3-16 characters"); return; }
    if (!regEmail.trim()) { showError("Please enter an email address"); return; }
    if (regPwd.length < 8) { showError("Password must be at least 8 characters"); return; }
    if (!PASSWORD_REGEX.test(regPwd)) { showError("Password doesn't meet requirements"); return; }
    if (regPwd.includes("#")) { showError("Password cannot contain the # character"); return; }
    if (!regCountry) { showError("Please select country"); return; }
    if (!regPhone.trim()) { showError("Please enter your phone number"); return; }
    if (!agreeTerms) { showError("Please accept Terms & Conditions"); return; }
    register(regUser, regEmail, regPwd, referral.trim() || "", regCountry, regPhone);
  };

  const handle2FA = (e) => {
    e.preventDefault();
    if (!twoFactorCode.trim()) { showError("Please enter verification code"); return; }
    verifyTwoFactor(tempLoginData.uid, twoFactorCode);
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="am-backdrop" onClick={close} />
      <div className="am-overlay">
        <div className="am-container">

          <button className="am-close" onClick={close}><X size={18} /></button>

          {/* LEFT - Image */}
          <div className="am-img-side">
            <img src={mode === "login" ? "/assets/images/bcgames/signin/signup.png" : "/assets/images/bcgames/signup/signup.png"} alt=""
              onError={(e) => { e.target.style.display = "none"; }} />
          </div>

          {/* RIGHT - Form */}
          <div className="am-form-side">

            {showTwoFactor ? (
              <>
                <h2 className="am-title">Two-Factor Authentication</h2>
                {error && <div className="am-error">{error}</div>}
                <form onSubmit={handle2FA} className="am-form">
                  <div className="am-field">
                    <input type="text" value={twoFactorCode} placeholder="Enter verification code"
                      onChange={(e) => { setTwoFactorCode(e.target.value); clearError(); }} required />
                  </div>
                  <button type="submit" className="am-btn" disabled={isLoading}>
                    {isLoading ? "Verifying..." : "Verify Code"}
                  </button>
                </form>
              </>
            ) : mode === "login" ? (
              !forgotMode ? (
                <>
                  <h2 className="am-title">Sign In</h2>
                  {error && <div className="am-error">{error}</div>}

                  <form onSubmit={handleLogin} className="am-form">
                    <div className="am-field">
                      <input type="text" value={loginUser} placeholder="Username or email"
                        onChange={(e) => { setLoginUser(e.target.value); clearError(); }} required />
                    </div>

                    <div className="am-field am-field-pwd">
                      <input type={showLoginPwd ? "text" : "password"} value={loginPwd}
                        placeholder="Password"
                        onChange={(e) => { setLoginPwd(e.target.value); clearError(); }} required />
                      <button type="button" className="am-eye" onClick={() => setShowLoginPwd(!showLoginPwd)}>
                        {showLoginPwd ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>

                    <a href="#" className="am-forgot" onClick={(e) => { e.preventDefault(); setForgotMode(true); }}>
                      Forgot your password?
                    </a>

                    <button type="submit" className="am-btn" disabled={isLoading}>
                      {isLoading ? "Signing in..." : "Sign In"}
                    </button>
                  </form>

                  <div className="am-divider"><span>or continue with</span></div>

                  <GoogleLoginButton />

                  <p className="am-switch">
                    Don't have an account?
                    <button type="button" onClick={switchToRegister} className="am-switch-link">Sign up</button>
                  </p>
                </>
              ) : (
                <>
                  <h2 className="am-title">Reset Password</h2>
                  {forgotSent ? (
                    <div className="am-success">
                      Reset link sent! Check your email.
                      <button type="button" className="am-forgot" style={{ display: "block", marginTop: 16 }}
                        onClick={() => { setForgotMode(false); setForgotSent(false); }}>Back to Sign In</button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgot} className="am-form">
                      <div className="am-field">
                        <input type="email" value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="Enter your email" required />
                      </div>
                      <button type="submit" className="am-btn" disabled={isLoading}>
                        {isLoading ? "Sending..." : "Reset Password"}
                      </button>
                      <button type="button" className="am-forgot" style={{ textAlign: "center" }}
                        onClick={() => setForgotMode(false)}>Back to Sign In</button>
                    </form>
                  )}
                </>
              )
            ) : (
              <>
                <h2 className="am-title">Sign Up</h2>
                {error && <div className="am-error">{error}</div>}

                <form onSubmit={handleRegister} className="am-form am-form-reg">
                  <div className="am-field">
                    <label>Username <span className="am-hint">(5-16 characters)</span></label>
                    <input type="text" value={regUser} placeholder="Choose a username"
                      onChange={(e) => { setRegUser(e.target.value); clearError(); }}
                      required minLength={5} maxLength={16} />
                  </div>

                  <div className="am-field">
                    <label>Email</label>
                    <input type="email" value={regEmail} placeholder="Enter your email"
                      onChange={(e) => { setRegEmail(e.target.value); clearError(); }} required />
                  </div>

                  <div className="am-field am-field-pwd">
                    <label>Password <span className="am-hint">(8+, upper+lower+num+special)</span></label>
                    <input type={showRegPwd ? "text" : "password"} value={regPwd}
                      placeholder="Create a password"
                      onChange={(e) => { setRegPwd(e.target.value); setPwdErrors(e.target.value ? validatePwd(e.target.value) : []); clearError(); }}
                      required minLength={8} />
                    <button type="button" className="am-eye" onClick={() => setShowRegPwd(!showRegPwd)}>
                      {showRegPwd ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                    {pwdErrors.length > 0 && (
                      <div className="am-pwd-hints">
                        {pwdErrors.map((e) => <span key={e} className="am-pwd-hint">{e}</span>)}
                      </div>
                    )}
                  </div>

                  <div className="am-field">
                    <label>Country</label>
                    <select value={regCountry} onChange={(e) => setRegCountry(e.target.value)}
                      className="am-select" required>
                      <option value="">Select Country</option>
                      <option value="India">India</option>
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Brazil">Brazil</option>
                      <option value="Japan">Japan</option>
                      <option value="South Korea">South Korea</option>
                      <option value="Singapore">Singapore</option>
                      <option value="UAE">UAE</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="South Africa">South Africa</option>
                      <option value="Philippines">Philippines</option>
                      <option value="Indonesia">Indonesia</option>
                      <option value="Thailand">Thailand</option>
                      <option value="Vietnam">Vietnam</option>
                      <option value="Bangladesh">Bangladesh</option>
                      <option value="Pakistan">Pakistan</option>
                      <option value="Nepal">Nepal</option>
                      <option value="Sri Lanka">Sri Lanka</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="am-field">
                    <label>Phone Number</label>
                    <input type="tel" value={regPhone}
                      onChange={(e) => setRegPhone(e.target.value)}
                      placeholder="Enter phone number" required />
                  </div>

                  <div className="am-field">
                    <label>Referral Code <span className="am-hint">(optional)</span></label>
                    <input type="text" value={referral}
                      onChange={(e) => setReferral(e.target.value)}
                      placeholder="Enter referral code" />
                  </div>

                  <label className="am-check">
                    <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} />
                    <span>I agree to the <Link to="/rules?tab=1" target="_blank">Terms</Link> and <Link to="/rules?tab=0" target="_blank">Privacy Policy</Link></span>
                  </label>

                  <button type="submit" className="am-btn" disabled={isLoading || !agreeTerms}>
                    {isLoading ? "Creating account..." : "Sign Up"}
                  </button>
                </form>

                <p className="am-switch">
                  Already have an account?
                  <button type="button" onClick={switchToLogin} className="am-switch-link">Sign In</button>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default AuthModal;
